import React, { useMemo, useState } from 'react';
import Slider, { Handle } from "rc-slider";
import Select from "components/elements/form/select/Select";
import Option from "components/elements/form/select/Option";
import { formatNumberToLocale } from "helpers";
import useRouter from "components/hooks/useRouter";
import Icon from "components/elements/Icon";
import cn from "classnames";
import useTrans from "components/hooks/useTrans";
import { Tier } from "components/pricing/calculator/Calculator_v2";

type Props = {
    tiers: Tier[];
    selectedPlan: any;
    sliderIndex: number;
    setSliderIndex: any;
    setSliderHasMoved: any;
    sliderHasMoved: boolean;
    currency: string;
};

export default function SelectTier_v2(props: Props) {
    const {
        tiers,
        sliderIndex,
        setSliderIndex,
        selectedPlan,
        setSliderHasMoved,
        currency
    } = props;
    const { locale } = useRouter();
    const { t } = useTrans();

    const [marks] = useState({
        'starter': [100, 10000],
        'professional': [50, 50000],
        'enterprise': [100, 500000]
    });

    const tooltipCn = cn(
        "absolute bottom-full -ml-12 bg-white shadow-md px-4 py-2 rounded-xl mb-4 flex flex-col text-center",
    );

    const selectedTier = useMemo(() => {
        if(!tiers) {
            return null;
        }

        if (tiers?.length < sliderIndex) {
            return tiers[tiers.length - 1];
        }

        return tiers[sliderIndex];
    }, [tiers, sliderIndex]);

    const formatMark = (value: string | number) => {
        return (
            <div className={''}>
                <span className={"text-sm text-primary-400"}>{value}</span>
            </div>
        );
    };

    const config = useMemo(() => {
        let ticks = {};
        tiers && tiers.forEach((tier, i) => {
            if (marks[selectedPlan.id.toLowerCase()].includes(tier.endingUnit)) {
                ticks = {
                    ...ticks,
                    [i]: formatMark(formatNumberToLocale(tier.endingUnit, locale))
                };
            }
        });
        return ticks;
    }, [tiers, locale]);

    if (!tiers) {
        return null;
    }

    return (
        <>
            <div className={"hidden lg:inline-block mt-20 mb-12"}>
                <Slider
                    min={0}
                    max={tiers.length - 1}
                    step={1}
                    value={sliderIndex}
                    onChange={(index) => {
                        setSliderHasMoved(true);
                        setSliderIndex(index);
                    }}
                    marks={config}
                    handle={(props) => {
                        const { dragging, ...restProps } = props;
                        const className = restProps.className;
                        return (
                        // @ts-ignore
                            <Handle
                                {...restProps}
                                value={sliderIndex}
                                //@ts-ignore
                                // dragging={dragging.toString()}
                                className={`${className} transform transition-all transition-delay-100`}
                            >
                                <div className={tooltipCn}>
                                    <span
                                        className={"font-medium text-2xl"}
                                    >
                                        {selectedTier.displayQuantity[currency]}
                                    </span>
                                    <span
                                        className={"uppercase text-purple-dark text-xs leading-loose tracking-widest opacity-60"}
                                    >
                                        {t('contacts')}
                                    </span>
                                </div>
                                <Icon
                                    url={"/static/icons/chevron-left-right.svg"}
                                    className={`bg-white h-6 w-6 ml-px`}
                                />
                            </Handle>
                        );
                    }}
                    trackStyle={{ backgroundColor: '#F6F7FE', height: 5, width: "100%" }}
                    railStyle={{ backgroundColor: '#F6F7FE', height: 5, width: "100%" }}
                    dotStyle={{ display: 'none', backgroundColor: '#3430FF' }}
                    handleStyle={{
                        borderColor: '#3430FF',
                        height: 28,
                        width: 28,
                        marginTop: -14,
                        backgroundColor: '#3430FF',
                        // mask: `url('/static/icons/chevron-left-right.svg') no-repeat center center`,
                        // WebkitMask: `url('/static/icons/chevron-left-right.svg') no-repeat center center`,
                    }}
                />
            </div>
            <div className="inline-block lg:hidden relative w-full mb-8">
                <Select
                    className={"block appearance-none w-full border border-gray-200 text-gray-500 pl-3 py-3 font-light rounded-md focus:outline-none focus:shadow-outline"}
                    name={"language-switcher"}
                    value={selectedTier.endingUnit}
                    onChange={(event) => {
                        const index = tiers.findIndex((t: Tier) => t.endingUnit == event.target.value);
                        setSliderIndex(index);
                        setSliderHasMoved(true);
                    }}
                >
                    {tiers.map((tier: any) => (
                        <Option
                            key={tier.endingUnit}
                            label={t(`upToXContacts`, { contacts: tier.displayQuantity[currency] })}
                            value={tier.endingUnit}
                        />
                    ))}
                </Select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                    <div className={""}>
                        <svg
                            className="h-4 w-4 rotate-180 fill-gray-500"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 3 20 20"
                        >
                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                        </svg>
                        <svg
                            className="h-4 w-4 fill-gray-500"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 3 20 20"
                        >
                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                        </svg>
                    </div>
                </div>
            </div>
        </>
    );
}